<template>
  <div class="purchase">
    <h1>Purchase Points</h1>
    <div class="purchase-layout" :class="{ 'active-layout': activePack }">
      <div class="coin-packs" :class="{ 'purchase-active': activePack }">
        <div
          v-for="pack in coinPacks"
          :key="pack.id"
          class="coin-pack"
          :class="{ hidden: activePack && activePack.id !== pack.id }"
          v-show="!activePack || activePack.id === pack.id"
        >
          <div class="pack-header">{{ pack.name }}</div>
          <div class="pack-content">
            <div class="coin-amount">
              <img src="@/assets/gold-coin.png" alt="Gold" class="coin-icon" />
              <span>{{ pack.goldCoins }} Gold Coins</span>
            </div>
            <div class="coin-amount bonus">
              <img
                src="@/assets/sweep-coin.png"
                alt="Sweeps"
                class="coin-icon"
              />
              <span>+ {{ pack.sweepsCoins }} Sweeps Coins</span>
            </div>
            <div class="pack-price">${{ pack.price }}</div>
            <button
              v-if="!activePack || activePack.id !== pack.id"
              @click="startPurchase(pack)"
              class="purchase-button"
            >
              Purchase Now
            </button>
          </div>
        </div>
        <button v-if="activePack" @click="resetPurchase" class="cancel-button">
          Cancel Purchase
        </button>
      </div>
      <div v-if="loading" class="loading-container">
        <div
          class="loading-spinner"
          :style="{ backgroundImage: `url(${casinoRouletteUrl})` }"
        ></div>
        <p>Setting up purchase...</p>
      </div>
      <div class="container-wrapper">
        <p v-if="updatingBalance" class="balance-update-message">
          Updating your balance, please wait...
        </p>
        <div id="container" :class="{ 'container-active': activePack }"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { useStore } from "vuex";
import { createPaymentIntent, getPaymentStatus } from "../api/vivamo-api";
import casinoRoulette from "@/assets/casino-roulette.png"; // Ensure the image is imported for the spinner

export default {
  name: "Purchase",
  data() {
    return {
      activePack: null,
      casinoRouletteUrl: casinoRoulette,
      coinPacks: [
        {
          id: 1,
          name: "Starter Pack",
          price: 5,
          goldCoins: 500,
          sweepsCoins: 50,
        },
        {
          id: 2,
          name: "Entry Pack",
          price: 10,
          goldCoins: 1000,
          sweepsCoins: 100,
        },
        {
          id: 3,
          name: "Popular Pack",
          price: 20,
          goldCoins: 2000,
          sweepsCoins: 200,
        },
        {
          id: 4,
          name: "Pricey Pack",
          price: 100,
          goldCoins: 10000,
          sweepsCoins: 1000,
        },
      ],
    };
  },
  setup() {
    const store = useStore();
    const loading = ref(false);
    const updatingBalance = ref(false);
    const activePack = ref(null);

    const startPurchase = async (pack) => {
      loading.value = true;
      activePack.value = pack;
      try {
        // Get logged-in user's username from localStorage
        const userData = JSON.parse(
          localStorage.getItem("demoUserData") || "{}",
        );
        const username = userData.username;

        if (!username) {
          throw new Error("User not logged in");
        }

        const response = await createPaymentIntent(
          activePack.value.price,
          username,
        );

        loading.value = false;
        console.log("Payment intent created:", response);

        store.state.vivamo.createElements(
          "#container",
          response.clientSecretKey,
        );
        store.state.vivamo.on("payment-created", (data) => {
          console.log("Payment created: ", data);
        });

        // When the payment has been indicated as completed by the SDK,
        // check the payment status via the backend API.
        store.state.vivamo.on("payment-completed", async (data) => {
          console.log("Payment completed, checking for results...");

          // Show balance update loading state
          loading.value = false;
          updatingBalance.value = true;

          try {
            const paymentStatus = await getPaymentStatus(
              response.paymentIntentId,
            );
            if (paymentStatus.status === "success") {
              console.log("Payment successful, updating user balance...");
              store.dispatch("purchase", {
                goldCoins: activePack.value.goldCoins,
                sweepsCoins: activePack.value.sweepsCoins,
              });

              // Add a small delay to show the balance update message
              setTimeout(() => {
                updatingBalance.value = false;
                resetPurchase();
              }, 1000);
            } else {
              console.error("Payment failed or pending:", paymentStatus);
              updatingBalance.value = false;
            }
          } catch (error) {
            console.error("Error checking payment status:", error);
            updatingBalance.value = false;
          }
        });
      } catch (error) {
        console.error("Error creating payment intent:", error);
      }
    };

    const resetPurchase = () => {
      activePack.value = null;
      updatingBalance.value = false;
      const container = document.querySelector("#container");
      if (container) {
        container.innerHTML = "";
      }
    };

    onMounted(async () => {
      await store.dispatch("initializeVivamo");
    });

    return {
      startPurchase,
      resetPurchase,
      activePack,
      loading,
      updatingBalance,
    };
  },
};
</script>

<style scoped>
.purchase {
  margin: 0 auto;
  max-width: 1200px;
  position: relative;
  min-height: 80vh;
}
.purchase-active {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
}
.purchase-layout {
  width: 100%;
}
.purchase-layout.active-layout {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2rem;
  align-items: flex-start;
}

.coin-packs {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
}

.coin-pack {
  background: var(--card-background);
  border-radius: 15px;
  overflow: hidden;
  transition: transform 0.3s ease;
  border: 1px solid var(--accent-color);
}
.coin-packs.purchase-active {
  display: block;
}

.coin-pack:hover {
  transform: scale(1.02);
}

.pack-header {
  background-color: var(--primary-color);
  color: var(--text-light);
  padding: 1rem;
  text-align: center;
  font-size: 1.2rem;
  font-weight: bold;
}

.pack-content {
  padding: 1.5rem;
  text-align: center;
}

.coin-amount {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0.5rem 0;
  font-size: 1.1rem;
}

.coin-icon {
  width: 24px;
  height: 24px;
  margin-right: 0.5rem;
}

.bonus {
  color: var(--accent-color);
}

.pack-price {
  font-size: 2rem;
  color: var(--accent-color);
  margin: 1rem 0;
}

.purchase-button {
  background-color: var(--secondary-color);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
}

.purchase-button:hover {
  background-color: var(--accent-color);
  color: var(--primary-color);
}

.hidden {
  display: none !important;
}

.container-active {
  min-height: 300px;
  max-width: 800px;
}
.cancel-button {
  background-color: #ff4444;
  color: white;
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
  width: 100%;
  max-width: 300px;
}

.cancel-button:hover {
  background-color: #ff6666;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.loading-spinner {
  width: 80px;
  height: 80px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  animation: spin 2s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Mobile responsive styles */
@media (max-width: 768px) {
  .purchase {
    padding: 0 1rem;
  }

  .purchase h1 {
    font-size: 1.8rem;
    text-align: center;
    margin-bottom: 1.5rem;
    color: var(--text-color);
  }

  .purchase-layout.active-layout {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .coin-packs {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1.5rem;
  }

  .coin-pack {
    border-radius: 12px;
  }

  .pack-header {
    padding: 0.8rem;
    font-size: 1.1rem;
  }

  .pack-content {
    padding: 1.2rem;
  }

  .coin-amount {
    font-size: 1rem;
    margin: 0.4rem 0;
  }

  .coin-icon {
    width: 20px;
    height: 20px;
    margin-right: 0.4rem;
  }

  .pack-price {
    font-size: 1.6rem;
    margin: 0.8rem 0;
  }

  .purchase-button {
    padding: 0.8rem 1.5rem;
    font-size: 0.9rem;
  }

  .cancel-button {
    padding: 0.7rem 1.2rem;
    font-size: 0.9rem;
    max-width: 250px;
  }

  .container-active {
    min-height: 250px;
    max-width: 100%;
  }

  .loading-container {
    padding: 1.5rem;
  }

  .loading-spinner {
    width: 60px;
    height: 60px;
  }
}

@media (max-width: 480px) {
  .purchase {
    padding: 0 0.75rem;
  }

  .purchase h1 {
    font-size: 1.6rem;
    margin-bottom: 1.2rem;
  }

  .purchase-layout.active-layout {
    gap: 1rem;
  }

  .coin-packs {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .coin-pack {
    border-radius: 10px;
    max-width: 400px;
    margin: 0 auto;
  }

  .pack-header {
    padding: 0.7rem;
    font-size: 1rem;
  }

  .pack-content {
    padding: 1rem;
  }

  .coin-amount {
    font-size: 0.9rem;
    margin: 0.3rem 0;
  }

  .coin-icon {
    width: 18px;
    height: 18px;
    margin-right: 0.3rem;
  }

  .pack-price {
    font-size: 1.4rem;
    margin: 0.6rem 0;
  }

  .purchase-button {
    padding: 0.7rem 1.2rem;
    font-size: 0.85rem;
  }

  .cancel-button {
    padding: 0.6rem 1rem;
    font-size: 0.85rem;
    max-width: 200px;
  }

  .container-active {
    min-height: 200px;
  }

  .loading-container {
    padding: 1rem;
  }

  .loading-spinner {
    width: 50px;
    height: 50px;
  }
}

/* Landscape mobile orientation */
@media (max-width: 768px) and (orientation: landscape) {
  .purchase h1 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }

  .purchase-layout.active-layout {
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  .coin-packs {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 1rem;
  }

  .coin-pack {
    border-radius: 8px;
  }

  .pack-header {
    padding: 0.6rem;
    font-size: 0.9rem;
  }

  .pack-content {
    padding: 0.8rem;
  }

  .coin-amount {
    font-size: 0.8rem;
    margin: 0.2rem 0;
  }

  .coin-icon {
    width: 16px;
    height: 16px;
  }

  .pack-price {
    font-size: 1.2rem;
    margin: 0.4rem 0;
  }

  .purchase-button {
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
  }

  .cancel-button {
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
    max-width: 150px;
  }

  .container-active {
    min-height: 180px;
  }
}

/* Very small screens */
@media (max-width: 360px) {
  .purchase {
    padding: 0 0.5rem;
  }

  .purchase h1 {
    font-size: 1.4rem;
  }

  .coin-pack {
    max-width: 300px;
  }

  .pack-header {
    padding: 0.6rem;
    font-size: 0.95rem;
  }

  .pack-content {
    padding: 0.8rem;
  }

  .coin-amount {
    font-size: 0.85rem;
  }

  .pack-price {
    font-size: 1.2rem;
  }

  .purchase-button {
    padding: 0.6rem 1rem;
    font-size: 0.8rem;
  }

  .cancel-button {
    max-width: 180px;
  }
}

/* Very large mobile screens */
@media (max-width: 768px) and (min-width: 600px) {
  .coin-packs {
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 1.8rem;
  }

  .pack-header {
    font-size: 1.15rem;
  }

  .coin-amount {
    font-size: 1.05rem;
  }

  .pack-price {
    font-size: 1.8rem;
  }

  .purchase-button {
    padding: 0.9rem 1.8rem;
    font-size: 0.95rem;
  }
}

/* Balance Update Message */
.container-wrapper {
  display: flex;
  flex-direction: column;
}

.balance-update-message {
  color: var(--accent-color);
  font-size: 1.1rem;
  text-align: center;
  margin: 0 0 1rem;
  padding: 0.75rem 1rem;
  background: var(--card-background);
  border: 1px solid var(--accent-color);
  border-radius: 8px;
}
</style>
